import FTAnalytic from "@/analyze/FTAnalytic";
import { getFileByUrl } from "@/api";
import { FaceDetectRes, POSITION, PROJECT_TYPE } from "@/constants";
import { useCommonStore } from "@/stores";
import { isValidImg, selectFile } from "@/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

const useSourceImgUpload = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const setSourceImgFile = useCommonStore(state => state.setSourceImgFile);
  const setSourceImgBlobUrl = useCommonStore(state => state.setSourceImgBlobUrl);
  const setSourceImgUrl = useCommonStore(state => state.setSourceImgUrl);
  const setImgEditorOptions = useCommonStore(state => state.setImgEditorOptions);
  const setTask = useCommonStore(state => state.setTask);
  
  const handle = async (source: File, result?: File) => {
    setLoading(true);

    const { default: FaceUtils } = await import("@/utils/FaceUtils");

    await FaceUtils.init();

    const current = result || source;
    FaceUtils.detect(current)
      .then(res => {
        switch (res) {
          case FaceDetectRes.NoFace: 
            // 埋点（打开图片失败）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: false,
                type: "upload",
                fail_reason: "noface",
                project_type: PROJECT_TYPE,
              },
            });

            if (result) {
              toast(t("face_result_no_face"));
            } else {
              toast(t("face_result_no_face"));
            }

            setLoading(false);
            break;
          case FaceDetectRes.TooManyFace:
            // 埋点（打开图片失败）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: false,
                type: "upload",
                fail_reason: "many faces",
                project_type: PROJECT_TYPE,
              },
            });

            if (result) {
              toast(t("face_result_multi_face"));
            } else {
              toast(t("face_result_multi_face"));
            }
            setImgEditorOptions({
              show: true,
              sourceFile: source,
              onResult: (value: File) => {
                handle(source, value);
              },
              onClose: () => {
                setImgEditorOptions({ show: false });
                setLoading(false);
              }
            });
            break;
          case FaceDetectRes.TooSmall:
            // 埋点（打开图片失败）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: false,
                type: "upload",
                fail_reason: "smallface",
                project_type: PROJECT_TYPE,
              },
            });

            if (result) {
              toast(t("face_result_too_small"));
            } else {
              toast(t("face_result_too_small"));
            }
            setImgEditorOptions({
              show: true,
              sourceFile: source,
              onResult: (value: File) => {
                handle(source, value);
              },
              onClose: () => {
                setImgEditorOptions({ show: false });
                setLoading(false);
              }
            });
            break;
          case FaceDetectRes.TooBig:
            // 埋点（打开图片失败）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: false,
                type: "upload",
                fail_reason: "bigface",
                project_type: PROJECT_TYPE,
              },
            });

            if (result) {
              toast(t("face_result_too_big"));
            } else {
              toast(t("face_result_too_big"));
            }

            setLoading(false);
            break;
          case FaceDetectRes.Success:
            // 埋点（打开图片成功）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: true,
                type: "upload",
                project_type: PROJECT_TYPE,
              },
            });
            
            setSourceImgFile(current);
            setSourceImgBlobUrl(URL.createObjectURL(current));
            setSourceImgUrl("");
            setImgEditorOptions({ show: false });
            setTask(null);

            setLoading(false);
            break;
          case FaceDetectRes.Unknown:
            // 埋点（打开图片失败）
            FTAnalytic.addAnalytic({
              type: 'ev',
              action: 'fotor_web$callback$swap_project_page_open',
              label: {
                position: POSITION,
                is_success: false,
                type: "upload",
                fail_reason: "format",
                project_type: PROJECT_TYPE,
              },
            });

            toast(t("unknown_error"));

            setLoading(false);
            break;
          default:
            setLoading(false);
            break;
        }
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }

  const handleClick = async () => {
    // 埋点（打开图片）
    FTAnalytic.addAnalytic({
			type: 'ev',
			action: 'fotor_web$click_up$swap_project_page_open',
			label: {
				position: POSITION,
				project_type: PROJECT_TYPE,
			},
		});

    selectFile({ accept: "image/*" })
      .then((files: any) => {
        const file = files[0];
        if (isValidImg(file)) {
          handle(file);
        } else {
          toast(t("img_format_error"));
        }
      })
      .catch(console.error)
  }

  
  useEffect(() => {
    (async () => {
      const queryString = window.location.search;

      // 创建一个 URLSearchParams 对象来处理查询参数
      const params = new URLSearchParams(queryString);

      const sourceImgUrl = params.get("source"); 
      if (sourceImgUrl && sourceImgUrl.startsWith("https://")) {
        const url = new URL(window.location.href);
        url.searchParams.delete("source");
        window.history.replaceState({}, "",url.toString());
        try {
          setLoading(true);
          const source = await getFileByUrl(sourceImgUrl as string);
          // console.log("source:", source);
          handle(source);
        } catch (error) {
          setLoading(false);
        }

      }
    })();
  }, []);
  
  return {
    loading,
    handleClick,
  }
}

export default useSourceImgUpload;